import ActionTextIcon from "@/components/icons/ActionTextIcon";
import AntIcon from "@/components/icons/AntIcon";
import InfoIcon from "@/components/icons/InfoIcon";
import PopconfirmDeleteIcon from "@/components/icons/PopconfirmDeleteIcon";
import Ellipsis from "@/components/views/Ellipsis";
import { paginationTableProps, tableStandardProps } from "@/utils/componentUtils";
import { formatLocaleDate } from "@/utils/formatUtils";
import { useFilterSearchParams } from "@/utils/hooksUtils";
import { URLQuery } from "@/utils/queryUtils";
import { cssVariables, numberOrZero } from "@/utils/utils";
import { Button, Divider, Popconfirm, Table } from "antd";
import { ColumnsType } from "antd/lib/table";
import dayjs from "dayjs";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { useDeleteAgentCompetence, useGetAgentCompetences } from "../../../../common/api/queries";
import { PageSizes, TableSizes } from "../../../../common/constants";
import type { UUID } from "../../../../typings/global";
import { AgentCompetenceType } from "../../enums";
import { AgentCompetence } from "../../types";
import { AgentCompetenceForm } from "../forms/AgentCompetenceForm";

interface Props {
  agentId: UUID;
  showAllColumns?: boolean;
  showActions?: boolean;
}

export const AgentCompetenceTable = ({ agentId, showAllColumns, showActions }: Props) => {
  const { t } = useTranslation();

  const navigate = useNavigate();

  const [competenceFormOpen, setCompetenceFormOpen] = useState<boolean>(false);
  const [competenceToUpdate, setCompetenceToUpdate] = useState<AgentCompetence>();

  const searchParams = useFilterSearchParams();

  const { data: competences } = useGetAgentCompetences(agentId);
  const deleteAgentCompetence = useDeleteAgentCompetence();

  const handleCompetenceUpdateClick = (competence: AgentCompetence) => {
    setCompetenceToUpdate(competence);
    setCompetenceFormOpen(true);
  };

  const handleCompetenceFormCancel = () => {
    setCompetenceToUpdate(undefined);
    setCompetenceFormOpen(false);
  };

  const columns: ColumnsType<AgentCompetence> = [
    {
      key: "type",
      title: t("agent.competence.enums.competenceType._label"),
      ellipsis: { showTitle: false },
      width: 80,
      render: (_, record) => <Ellipsis>{t("agent.competence.enums.competenceType." + record.type)}</Ellipsis>,
    },
    {
      key: "label",
      title: t("agent.competence.attrs.label"),
      ellipsis: { showTitle: false },
      render: (_, record) =>
        record.type === AgentCompetenceType.OTHER ? (
          <Ellipsis>{record.label}</Ellipsis>
        ) : (
          <Ellipsis>
            {`${t("agent.enums.financialSector." + record.sector)} | ${t(
              "agent.competence.helpers.competenceLevel." + record.level,
            )}`}
          </Ellipsis>
        ),
    },

    {
      key: "startDate",
      title: t("agent.competence.attrs.intervals.startDate"),
      width: 100,
      render: (_, record) => (
        <>
          {record.intervals.map((i) => (
            <React.Fragment key={i.id}>
              {formatLocaleDate(i.startDate)}
              <br />
            </React.Fragment>
          ))}
        </>
      ),
    },
    {
      key: "endDate",
      title: t("agent.competence.attrs.intervals.endDate"),
      width: 100,
      render: (_, record) => (
        <>
          {record.intervals.map((i) => (
            <React.Fragment key={i.id}>
              {formatLocaleDate(i.endDate)}
              <br />
            </React.Fragment>
          ))}
        </>
      ),
    },
    {
      key: "provider",
      title: t("agent.competence.enums.competenceProvider._label"),
      ellipsis: { showTitle: false },
      width: 190,
      render: (_, record) =>
        record.intervals.map((interval) => (
          <div key={interval.id}>
            {interval.provider ? (
              <Ellipsis>{t("agent.competence.enums.competenceProvider." + interval.provider)}</Ellipsis>
            ) : (
              <br />
            )}
          </div>
        )),
    },
    {
      key: "active",
      title: t("agent.competence.helpers.active"),
      align: "center",
      width: 70,
      render: (_, record) =>
        record.intervals.some(
          (i) => dayjs().isSameOrAfter(i.startDate, "day") && dayjs().isSameOrBefore(i.endDate, "day"),
        ) ? (
          <AntIcon type="check-circle-two-tone" twoToneColor={cssVariables.colorGreen} />
        ) : (
          <AntIcon type="close-circle-two-tone" twoToneColor={cssVariables.colorRed} />
        ),
    },
  ];

  if (showAllColumns) {
    columns.push({
      key: "processed",
      title: t("agent.competence.attrs.intervals.processed"),
      align: "center",
      width: 100,
      render: (_, record) => (
        <>
          {record.intervals.map((i) => (
            <React.Fragment key={i.id}>
              <AntIcon type={i.processed ? "check" : "close-circle"} />
              <br />
            </React.Fragment>
          ))}
        </>
      ),
    });

    columns.push({
      key: "note",
      align: "center",
      width: 40,
      render: (_, record) =>
        record.note ? <InfoIcon title={t("agent.competence.attrs.note")} tooltip={record.note} /> : null,
    });

    if (showActions) {
      columns.push({
        key: "actions",
        align: "right",
        fixed: "right",
        width: 180,
        render: (_, record) => (
          <>
            <ActionTextIcon
              icon="edit"
              color="blue"
              text={t("common.edit")}
              onClick={() => handleCompetenceUpdateClick(record)}
            />

            <Divider orientation="vertical" />

            <Popconfirm
              title={t("agent.competence.helpers.deleteConfirm")}
              icon={<PopconfirmDeleteIcon />}
              okText={t("common.yes")}
              cancelText={t("common.no")}
              okType="danger"
              onConfirm={() => deleteAgentCompetence.mutate({ id1: agentId, id2: record.id })}
            >
              <ActionTextIcon icon="delete" color="red" text={t("common.delete")} />
            </Popconfirm>
          </>
        ),
      });
    }
  }

  return (
    <>
      <Table<AgentCompetence>
        {...tableStandardProps()}
        scroll={{ x: TableSizes.SMALL }}
        columns={columns}
        dataSource={competences}
        pagination={{
          ...paginationTableProps,
          current: numberOrZero(searchParams.pageIndex) + 1,
          pageSize: PageSizes.LARGE,
          total: competences?.length,
          onChange: (pageIndex) => navigate({ search: URLQuery.getTableChangePageIndex(pageIndex) }, { replace: true }),
        }}
      />

      {showActions && (
        <>
          <div className={competences?.length ? "table-footer-margin" : "margin-top-small"}>
            <Button
              type="primary"
              size="small"
              icon={<AntIcon type="plus" />}
              onClick={() => setCompetenceFormOpen(true)}
            >
              {t("common.add")}
            </Button>
          </div>

          <AgentCompetenceForm
            open={competenceFormOpen}
            agentId={agentId}
            competence={competenceToUpdate}
            onFormCancel={handleCompetenceFormCancel}
          />
        </>
      )}
    </>
  );
};
